// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
import { Check, Close } from 'flowbite-react-icons/outline'
import type { OrderStep } from './OrderTypes'

// ─── Public types ──────────────────────────────────────────────────────────

/**
 * Controls label colour in tracker mode. Each tracker screen uses a distinct
 * tone to preserve its established visual hierarchy without coupling consumers
 * to each other's style choices.
 *
 * - `default` (OrderInProgress): cancelled → error colour; current → brand;
 *   all others → heading grey.
 * - `guidance` (OrderGuidance): current → brand; all others → heading grey
 *   with an additional dark-mode white override.
 * - `result` (OrderResultReady): current → brand; completed → heading grey;
 *   pending → muted grey (signals "not yet reached").
 */
export type LabelTone = 'default' | 'guidance' | 'result'

interface WizardProps {
  variant: 'wizard'
  steps: OrderStep[]
  onStepClick?: (stepId: string) => void
  isNavigationDisabled?: boolean
  isLoading?: boolean
}

interface TrackerProps {
  variant: 'tracker'
  steps: OrderStep[]
  /** Defaults to `'default'`. */
  labelTone?: LabelTone
}

export type OrderStepsRowProps = WizardProps | TrackerProps

// ─── Entry point ───────────────────────────────────────────────────────────

export function OrderStepsRow(props: OrderStepsRowProps): JSX.Element {
  if (props.variant === 'wizard') {
    return <WizardRow {...props} />
  }
  return <TrackerRow {...props} />
}

// ─── Tracker ──────────────────────────────────────────────────────────────

function TrackerRow({ steps, labelTone = 'default' }: TrackerProps) {
  const connectorClass =
    labelTone === 'guidance'
      ? 'absolute left-4 right-4 top-3.5 h-px bg-border-base dark:bg-gray-700'
      : 'absolute left-4 right-4 top-3.5 h-px bg-border-base'

  return (
    <div className="relative flex w-full justify-between">
      <div className={connectorClass} />
      {steps.map((step) => {
        const Icon = step.icon
        // `iconOverride` wins over the status-based default (including the
        // default Check for completed steps). Used by callers to signal
        // exceptional outcomes, e.g. excluded guidance (shows Minus inside
        // a completed dot rather than a misleading checkmark).
        const OverrideIcon = step.iconOverride
        return (
          <div key={step.id} className="relative z-10 flex flex-col items-center gap-1">
            <div
              className={
                step.status === 'completed'
                  ? 'timeline-dot-completed'
                  : step.status === 'cancelled'
                    ? 'timeline-dot-cancelled'
                    : step.status === 'current'
                      ? 'timeline-dot-active'
                      : 'timeline-dot-pending'
              }
            >
              {step.status === 'cancelled' ? (
                <Close className="h-3.5 w-3.5" />
              ) : OverrideIcon ? (
                <OverrideIcon className="h-3.5 w-3.5" />
              ) : step.status === 'completed' ? (
                <Check className="h-3.5 w-3.5" />
              ) : Icon ? (
                <Icon className="h-3.5 w-3.5" />
              ) : null}
            </div>
            <span
              className={`max-w-[80px] text-center text-[10px] leading-4 sm:max-w-none sm:text-xs ${trackerLabelClass(step.status, labelTone)}`}
            >
              {step.label}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function trackerLabelClass(status: OrderStep['status'], tone: LabelTone): string {
  if (status === 'cancelled') {
    return 'font-medium text-error-600 dark:text-error-500'
  }
  if (status === 'current') {
    return 'font-bold text-text-fg-brand dark:text-brand-400'
  }
  switch (tone) {
    case 'guidance':
      return 'font-medium text-text-heading dark:text-white'
    case 'result':
      return status === 'completed'
        ? 'font-medium text-text-heading dark:text-white'
        : 'font-medium text-text-subtle dark:text-gray-500'
    default:
      return 'font-medium text-text-heading dark:text-white'
  }
}

// ─── Wizard ───────────────────────────────────────────────────────────────

/**
 * Wizard variant: a lilac pill naming the current step, above a row of small
 * dots (current filled, rest hollow). Labels are omitted from the dots; each
 * button carries an aria-label with its step name for screen readers.
 *
 * Tracker-only features (`cancelled` status, per-step icon overrides) are not
 * supported here. Passing either renders a visible error and logs to console —
 * never silently degrades to a neutral dot.
 */
function WizardRow({ steps, onStepClick, isNavigationDisabled = false, isLoading = false }: WizardProps) {
  const violations: string[] = []
  if (steps.some((s) => s.status === 'cancelled')) violations.push('cancelled step status')
  if (steps.some((s) => s.iconOverride !== undefined)) violations.push('per-step iconOverride')

  if (violations.length > 0) {
    const msg = `OrderStepsRow (wizard): tracker-only feature(s) passed — ${violations.join(', ')}. Use variant="tracker" for these steps.`
    console.error(msg)
    return (
      <div role="alert" aria-live="assertive" className="rounded border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 px-3 py-2 text-xs font-medium text-text-fg-error dark:text-error-100">
        Stepper configuration error — see console for details.
      </div>
    )
  }

  const isClickable = !!onStepClick && !isNavigationDisabled && !isLoading
  const currentStep = steps.find((s) => s.status === 'current')

  return (
    <div className={`flex flex-col gap-1 ${isNavigationDisabled || isLoading ? 'opacity-60' : ''}`}>
      {currentStep && (
        <span className="inline-flex self-start rounded-full bg-brand-100 dark:bg-gray-800 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-400">
          {currentStep.label}
        </span>
      )}

      <div className="relative flex w-full items-center justify-between">
        <div className="absolute left-2 right-2 top-1/2 h-px -translate-y-1/2 bg-border-base" />
        {steps.map((step) => (
          <button
            key={step.id}
            aria-label={step.label}
            onClick={() => isClickable && onStepClick?.(step.id)}
            disabled={!isClickable}
            className={`group relative z-10 p-0.5 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div
              className={`rounded-full transition-transform ${
                step.status === 'current'
                  ? 'h-3 w-3 bg-brand-600'
                  : step.status === 'completed'
                    ? 'h-2.5 w-2.5 border-2 border-brand-600 dark:border-brand-500 bg-white dark:bg-gray-900'
                    : 'h-2.5 w-2.5 border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900'
              } ${isClickable ? 'group-hover:scale-125' : ''}`}
            />
          </button>
        ))}
      </div>
    </div>
  )
}
